'use client';

import { useState } from "react";
import Link from "next/link";
import { useRouter } from 'next/navigation';
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Info } from "lucide-react";
import api from "@/utils/api";
import axios, { AxiosError } from "axios";

// 定义API响应类型
interface ApiResponse {
  code?: number;
  data?: Record<string, unknown>;
  message?: string;
  detail?: string;
}

export default function RegisterPage() {
  const router = useRouter();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [organization, setOrganization] = useState('');
  const [address, setAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    
    // Basic validation
    if (!email.trim() || !firstName.trim() || !lastName.trim() || !title.trim() || !organization.trim() || !address.trim() || !phoneNumber.trim()) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    if (!email.includes('@')) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    if (firstName.includes('_') || lastName.includes('_')) {
      toast({
        title: "Invalid Name",
        description: "First name and last name may contain spaces, but cannot contain underscores (_).",
        variant: "destructive",
      });
      return;
    }

    const nickname = `${firstName.trim()}_${lastName.trim()}`;
    if (nickname.length > 64) {
      toast({
        title: "Name Too Long",
        description: "First name and last name must be 63 characters or fewer in total.",
        variant: "destructive",
      });
      return;
    }



    if (title.trim().length > 100) {
      toast({
        title: "Title Too Long",
        description: "Your job title must be 100 characters or fewer.",
        variant: "destructive",
      });
      return;
    }

    if (address.trim().length > 500) {
      toast({
        title: "Address Too Long",
        description: "Company/organization address must be 500 characters or fewer.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Prepare registration data 
      const registrationData = {
        email: email.trim(),
        username: email.trim(),
        nickname,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        title: title.trim(),
        organization: organization.trim(),
        address: address.trim(),
        phone: phoneNumber.trim(),
      };

      // 打印看看提交的数据
      console.log("Submitting registration data:", registrationData);

      // Call the registration API
      const register_response = await api.post('/api/user/registration/register_request', registrationData);
      
      console.log("register_response");
      console.log(register_response);
      const responseData = register_response as unknown as ApiResponse;

      // 检查响应数据
      if (responseData) {
        toast({
          title: "Registration Submitted",
          description: "Your registration request has been submitted. You will receive an email with login details once approved.",
          variant: "default",
        });
        
        // Redirect to login page after successful submission
        router.push('/login');
      }
    } catch (error: unknown) {
      console.error('Registration error:', error);
      
      // Handle API error response
      let errorMessage = "Unable to submit your registration. Please try again later.";
      
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiResponse>;
        const responseData = axiosError.response?.data;
        console.log("responseDatacatch");
        console.log(responseData);
        
        // 确保错误信息是字符串
        if (responseData?.detail) {
          if (typeof responseData.detail === 'string') {
            errorMessage = responseData.detail;
          } else {
            errorMessage = JSON.stringify(responseData.detail);
          }
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
      
      toast({
        title: "Registration Failed",
        description: typeof errorMessage === 'string' ? errorMessage : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative isolate h-dvh w-full overflow-y-auto overflow-x-hidden">
      {/* Background image */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-indigo-900/70 to-purple-900/70"></div>
        <img
          src="/images/login-bg.jpg"
          alt="Background"
          className="h-full w-full object-cover opacity-60 mix-blend-overlay"
        />
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center opacity-20"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-[128px] opacity-20"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600 rounded-full filter blur-[128px] opacity-20"></div>
      </div>
      
      <div className="flex min-h-full w-full items-center justify-center px-4 py-3 sm:px-6">
        <Card className="w-full max-w-3xl shrink-0 bg-white/95 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader className="p-4 pb-3 sm:px-6">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 space-y-1.5">
                <CardTitle className="text-xl sm:text-2xl">Create an Account</CardTitle>
                <CardDescription>
                  Enter your information to register for KINET
                </CardDescription>
              </div>
              <Link href="/" aria-label="KINET home" className="shrink-0">
                <img
                  src="/images/logo.png"
                  alt="Company Logo"
                  width={120}
                  height={40}
                  className="h-auto w-20 sm:w-24"
                />
              </Link>
            </div>
          </CardHeader>

          <CardContent className="px-4 pb-4 sm:px-6 sm:pb-5">
            <Alert className="mb-3 bg-blue-50 text-blue-800 border-blue-200">
              <Info className="h-4 w-4" />
              <AlertTitle className="text-sm font-medium">Registration Approval Required</AlertTitle>
              <AlertDescription className="text-xs">
                All registrations require admin approval. A password will be automatically generated and sent to your email once approved.
              </AlertDescription>
            </Alert>

            {submitError && (
              <Alert className="mb-4 bg-red-50 text-red-800 border-red-200">
                <Info className="h-4 w-4" />
                <AlertTitle className="text-sm font-medium">Registration Error</AlertTitle>
                <AlertDescription className="text-xs">
                  {typeof submitError === 'string' ? submitError : "An error occurred during registration"}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-x-5 gap-y-2.5 sm:grid-cols-2">
              <div className="grid min-w-0 gap-1.5">
                <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Given name"
                  required
                  maxLength={63}
                  pattern="[^_]*"
                  title="Spaces are allowed, but underscores (_) are not."
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="grid min-w-0 gap-1.5">
                <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Family name"
                  required
                  maxLength={63}
                  pattern="[^_]*"
                  title="Spaces are allowed, but underscores (_) are not."
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="grid min-w-0 gap-1.5">
                <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">
                  Please use the email address provided by your organization.
                </p>
              </div>

              <div className="grid min-w-0 content-start gap-1.5">
                <Label htmlFor="phoneNumber">Phone Number <span className="text-red-500">*</span></Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  placeholder="+1234567890"
                  required
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="grid min-w-0 gap-1.5">
                <Label htmlFor="organization">Company/Organization <span className="text-red-500">*</span></Label>
                <Input
                  id="organization"
                  type="text"
                  placeholder="Your company or institution"
                  required
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="grid min-w-0 gap-1.5">
                <Label htmlFor="title">Job Title/Position <span className="text-red-500">*</span></Label>
                <Input
                  id="title"
                  name="title"
                  type="text"
                  autoComplete="organization-title"
                  placeholder="Your role in the organization"
                  required
                  maxLength={100}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="grid min-w-0 gap-1.5 sm:col-span-2">
                <Label htmlFor="address">Company/Organization Address <span className="text-red-500">*</span></Label>
                <Input
                  id="address"
                  name="address"
                  type="text"
                  autoComplete="street-address"
                  placeholder="Street, city, state/province, postal code, country"
                  required
                  maxLength={500}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={loading}
                />
              </div>

              <Button
                type="submit"
                className="mt-1 w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-600/40 border-0 transition-all duration-300 sm:col-span-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting Registration...
                  </>
                ) : (
                  "Submit Registration"
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex justify-center border-t px-4 py-3 sm:px-6">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <Link href="/login" className="text-blue-600 hover:underline font-medium">
                Log in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
